import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import CalendarUtils from 'services/CalendarUtils';
import careyIcon from 'assets/img/icons/avatar-ai.svg';
import {
  isCareyMessage,
  isApprovalRequest,
  isHealthRecordArtifact,
  isAppointmentsArtifact,
  isDocumentsArtifact,
  isImmunizationsArtifact,
  isHandoffArtifact,
  isDocumentReceivedArtifact,
  isSubscriptionArtifact,
  isChoosePrimaryArtifact,
  isCancellationListArtifact,
  getArtifactId,
  getArtifactStatus,
  getArtifactPayload,
  getApprovalExpiresAt,
  parseCareySummarySections,
  CAREY_APPROVAL_STATUS,
} from 'constants/carey';
import ErrorBoundary from 'components/Utils/ErrorBoundary';
import './style.scss';
import Icon from 'components/Utils/NewIcon/index';
import { FILE_CMD } from 'hooks/useChat';
import MediaChatMessage from 'components/Chat/ChatWindow/ChatMessage/MediaChatMessage';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DefaultTooltip from 'components/Utils/DefaultTooltip';
import Linkify from 'linkify-react';
import PatientBadge from 'components/Patient/PatientBadge';
import { useAppSelector } from 'careand-redux/store';
import DocumentBadge from 'components/HealthRecords/Documents/DocumentBadge';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import ChoosePrimaryPage from 'scenes/ChoosePrimary';
import DefaultPopup from 'components/Utils/DefaultPopup';
import { ActivePatient } from 'careand-redux/selectors';
import { CancellationListForm } from 'components/Schedule/CancellationListForm';
import AppointmentUtils from 'services/AppointmentUtils';
import StaffUtils from 'services/StaffUtils';
import DocumentUtils from 'services/DocumentUtils';
import { DocumentItems } from 'scenes/PatientHealthRecords/RecordsTimeline/Detail';
import InlineRecordExpander from './InlineRecordExpander';
import DefaultModal from 'components/Utils/DefaultModal';
import LargeResourcePreview from 'components/Utils/LargeResourcePreview';
import PatientAddCardModal from 'components/Payments/CreditCardScreen/PatientAddCardModal';
import { SummaryCard } from 'scenes/PatientHealthRecords/Summary';
import { getHealthSummary, SUMMARY_SECTION_DATA } from 'scenes/PatientHealthRecords/Summary/model';
import { useNavStack, useNavTab } from 'careand-redux/slices/ui/hooks';
import { Stacks, Tabs } from 'careand-redux/slices/ui/navigation';

const dateFormat = 'h:mmA Do MMM YYYY';

const ChatMessage = ({ name, author, date, messages, own, roomName, patient }) => {
  const isCarey = isCareyMessage(messages?.[0]);
  const className = ['chat-message', own && 'own', isCarey && 'carey'].filter(Boolean).join(' ');
  return (
    <div className={className}>
      <div className="border" />
      <div className="message-block">
        <span className="name">
          {isCarey && <img className="carey-badge" src={careyIcon} alt="AI" />}
          {name ? name : author}
        </span>
        <span className="date">{CalendarUtils.format(date, dateFormat)}</span>
        <br />
        <MessageElement messages={messages} patient={patient} roomName={roomName} />
      </div>
    </div>
  );
};

const MessageElement = ({ messages, roomName, patient }) => {
  return messages
    .filter((message) => !!message.text || message.message)
    .map((message, index) => {
      let content;

      if (isMediaMessage(message)) {
        content = <MediaMessage message={message} patient={patient} roomName={roomName} />;
      } else if (message.markdown) {
        content = <MarkdownMessage message={message} />;
      } else {
        content = <TextMessage message={message} />;
      }

      // Approval-request messages duplicate their summary in both the chat bubble body and the
      // approval card. Suppress the bubble's text content (only) for approval requests; sections
      // and the lastRead indicator still render normally so we don't lose those affordances.
      const isApproval = isApprovalRequest(message);
      // Launcher cards (choose-primary, cancellation-list) are fully self-describing
      // (title + button), so their message body text would just duplicate the card.
      const isLauncherCard =
        isChoosePrimaryArtifact(message) || isCancellationListArtifact(message);

      return (
        <React.Fragment key={index}>
          <pre className="message">
            {message.context || isApproval || isLauncherCard ? null : content}
            {message.sections?.map(({ title, content }) => (
              <Section title={title} content={content} />
            ))}
            {message.lastRead === true && (
              <div className="last-read animated fadeIn">
                <Icon src="check" className="blue" width="15px" alt="Read" />
              </div>
            )}
          </pre>
          {isApproval && <CareyApprovalCard message={message} />}
          {isHealthRecordArtifact(message) && <CareyHealthRecordCard message={message} />}
          {isAppointmentsArtifact(message) && <CareyAppointmentsCard message={message} />}
          {isDocumentsArtifact(message) && <CareyDocumentsCard message={message} />}
          {isImmunizationsArtifact(message) && <CareyImmunizationsCard message={message} />}
          {isHandoffArtifact(message) && <CareyHandoffCard message={message} />}
          {isDocumentReceivedArtifact(message) && <CareyDocumentReceivedCard message={message} />}
          {isSubscriptionArtifact(message) && <CareySubscriptionCard message={message} />}
          {isChoosePrimaryArtifact(message) && <CareyChoosePrimaryCard message={message} />}
          {isCancellationListArtifact(message) && <CareyCancellationListCard message={message} />}
        </React.Fragment>
      );
    });
};

const approvalStatusLabel = (status) => {
  switch (status) {
    case CAREY_APPROVAL_STATUS.approved:
    case CAREY_APPROVAL_STATUS.executed:
      return '✓ Approved';
    case CAREY_APPROVAL_STATUS.denied:
      return 'Declined';
    case CAREY_APPROVAL_STATUS.expired:
      return 'This request expired';
    default:
      return '';
  }
};

// Approval card for a Carey mutating action (book/reschedule/cancel). The action is performed
// server-side ONLY when the patient approves here — Carey itself cannot execute it. Approval goes
// through a dedicated endpoint (not a chat reply), so a stale/jailbroken model can't fake it.
const CareyApprovalCard = ({ message }) => {
  const dispatch = useAppDispatch();
  const requestId = getArtifactId(message);
  const payload = getArtifactPayload(message);
  // Local status takes over once the patient acts (the persisted message attributes may be stale
  // until the chat reloads).
  const [status, setStatus] = useState(getArtifactStatus(message) || CAREY_APPROVAL_STATUS.pending);
  // Tracks which action is in flight ('approve' | 'deny' | null) so we can spinner the clicked
  // button only and disable the other.
  const [busyAction, setBusyAction] = useState(null);
  const busy = busyAction !== null;

  // Time-sensitive cards (book/reschedule) carry `expires_at` — the proposed slot isn't held, so
  // once it passes the server rejects the approval. Flip the card to expired client-side at that
  // moment so the patient never taps a dead Approve button (the server stays the source of truth on
  // tap). A timer re-renders the card right as the clock crosses expires_at.
  const expiresAt = getApprovalExpiresAt(message);
  const [expired, setExpired] = useState(expiresAt !== undefined && moment().unix() > expiresAt);
  useEffect(() => {
    if (expiresAt === undefined || expired) return undefined;
    const msUntilExpiry = (expiresAt - moment().unix()) * 1000;
    const timer = setTimeout(() => setExpired(true), Math.max(0, msUntilExpiry));
    return () => clearTimeout(timer);
  }, [expiresAt, expired]);

  // Booking-fee fetch for `book` approvals. The backend enriches `payload.proposal` with an
  // appointment-shaped object that `getBookingFee` can price directly. Mirrors the
  // AppointmentBooking/Review on-mount fetch.
  const activeUserId = useAppSelector((state) => state.authentication.activeUserId);
  const [fee, setFee] = useState(null);
  const [feeLoading, setFeeLoading] = useState(false);

  // The patient the proposed booking is for (defaults to the logged-in patient if the proposal
  // doesn't carry one — same fallback as the fee fetch).
  const billedPatientId = payload?.proposal?.patient_id || activeUserId;
  const billedPatient = useAppSelector((state) =>
    billedPatientId ? state.patients.byId[billedPatientId] : undefined,
  );
  const billedPatientCards = useAppSelector((state) =>
    billedPatientId ? state.payments.creditCards[billedPatientId] : undefined,
  );
  const [showAddCard, setShowAddCard] = useState(false);

  // Fetch credit cards for the billed patient so we can decide whether to surface the "Add credit
  // card" affordance. Only relevant for pending `book` approvals.
  useEffect(() => {
    if (status !== CAREY_APPROVAL_STATUS.pending) return;
    if (payload?.action !== 'book') return;
    if (!billedPatientId) return;
    if (billedPatientCards !== undefined) return; // already loaded
    dispatch(api.Payments.getCreditCards({ patientId: billedPatientId }));
  }, [dispatch, status, payload, billedPatientId, billedPatientCards]);

  useEffect(() => {
    // Once the request is confirmed/declined the price is locked server-side, so there's nothing to
    // price dynamically anymore — skip the fee fetch for any non-pending (resolved) approval.
    if (status !== CAREY_APPROVAL_STATUS.pending) return;
    if (payload?.action !== 'book') return;
    const proposal = payload?.proposal ?? {};
    if (!proposal.concern_id || !proposal.date) return;
    // patient_id is optional in the proposal (server defaults to the logged-in patient); fall back
    // to the authenticated user for the FE call.
    const userId = proposal.patient_id || activeUserId;
    if (!userId) return;
    setFeeLoading(true);
    dispatch(
      api.Appointments.getBookingFee({
        userId,
        appointment: proposal,
      }),
      { errorTitle: 'Booking Fee' },
    )
      .then((result) => {
        if (result) setFee(result.booking_value);
      })
      .finally(() => setFeeLoading(false));
  }, [dispatch, payload, activeUserId, status]);

  const respond = async (decision) => {
    if (busy || !requestId) return;
    setBusyAction(decision);
    const result = await dispatch(
      api.Chat.respondToCareyApproval({
        requestId,
        careyApprovalDecision: { decision },
      }),
      { errorTitle: 'Carey' },
    );
    if (result?.status) {
      setStatus(result.status);
    } else {
      setBusyAction(null);
    }
  };

  // A locally-expired card renders exactly like a server-resolved 'expired' one (label + no
  // buttons), without waiting for the patient to tap and the server to tell us.
  const effectiveStatus =
    expired && status === CAREY_APPROVAL_STATUS.pending ? CAREY_APPROVAL_STATUS.expired : status;
  const pending = effectiveStatus === CAREY_APPROVAL_STATUS.pending;
  const showFee = payload?.action === 'book';
  // Show "Add credit card" only when we know there's a real fee AND the billed patient currently
  // has no card on file. We require cards to be loaded (Array.isArray) so we don't flash the button
  // before the fetch completes, and the patient object so the modal can render.
  const needsCard =
    pending &&
    showFee &&
    !feeLoading &&
    typeof fee === 'number' &&
    fee > 0 &&
    Array.isArray(billedPatientCards) &&
    billedPatientCards.length === 0 &&
    !!billedPatient;

  let feeLabel = null;
  if (showFee) {
    if (feeLoading) {
      feeLabel = 'Checking fee...';
    } else if (fee === null || fee === undefined) {
      feeLabel = null;
    } else if (fee === 0) {
      feeLabel = 'Included in your plan';
    } else {
      feeLabel = `$${fee}`;
    }
  }

  // Action-specific icon so the card carries a visual cue even before the patient reads the
  // summary. Falls back to the generic clock-calendar.
  const approvalIcon =
    {
      book: 'calendar-plus',
      cancel: 'calendar-xmark',
      reschedule: 'calendar-clock',
      cancel_subscription: 'wallet',
    }[payload?.action] || 'calendar-clock';

  return (
    <div className="carey-artifact carey-approval">
      <div className="carey-approval-icon" aria-hidden="true">
        <Icon src={approvalIcon} width="16px" height="16px" alt="" />
      </div>
      <div className="carey-approval-body">
        <div className="carey-approval-text">
          {payload?.summary && <div className="carey-approval-summary">{payload.summary}</div>}
          {showFee && pending && feeLabel && <div className="carey-approval-fee">{feeLabel}</div>}
        </div>
        <div className="carey-approval-side">
          {pending ? (
            <div className="carey-approval-actions">
              <button
                type="button"
                className="carey-approve"
                disabled={busy}
                onClick={() => respond('approve')}
              >
                {busyAction === 'approve' && <span className="carey-spinner" aria-hidden="true" />}
                Approve
              </button>
              <button
                type="button"
                className="carey-deny"
                disabled={busy}
                onClick={() => respond('deny')}
              >
                {busyAction === 'deny' && <span className="carey-spinner" aria-hidden="true" />}
                Not now
              </button>
              {needsCard && (
                <button
                  type="button"
                  className="carey-add-card"
                  disabled={busy}
                  onClick={() => setShowAddCard(true)}
                >
                  Add credit card
                </button>
              )}
            </div>
          ) : (
            <div className={`carey-approval-resolved ${effectiveStatus}`}>
              {approvalStatusLabel(effectiveStatus)}
            </div>
          )}
        </div>
      </div>
      {billedPatient && (
        <PatientAddCardModal
          showModal={showAddCard}
          openModal={setShowAddCard}
          patient={billedPatient}
          onAddSuccess={() => {
            // Refresh the cards list so the "Add credit card" button hides once a card is added.
            if (billedPatientId) {
              dispatch(api.Payments.getCreditCards({ patientId: billedPatientId }));
            }
            setShowAddCard(false);
          }}
        />
      )}
    </div>
  );
};

// Read-only health-record summary. The artifact carries only a patient_id reference (never the
// record data — that would bloat the chat item and duplicate PHI); the patient-filtered record is
// fetched through the normal health-records API. Rendering reuses the patient app's own health
// summary cards (SummaryCard + getHealthSummary), so it stays visually identical to the Health
// Records → Summary page.
const CareyHealthRecordCard = ({ message }) => {
  const dispatch = useAppDispatch();
  const patientId = getArtifactPayload(message)?.patient_id;
  const patientData = useAppSelector((state) =>
    patientId ? state.healthRecords.byId[patientId] : undefined,
  );

  useEffect(() => {
    if (patientId && !patientData) {
      dispatch(api.HealthRecords.getCPP({ patientId }));
    }
  }, [patientId, patientData]);

  if (!patientId) return null;
  if (!patientData) {
    return (
      <div className="carey-artifact carey-health-record">
        <span className="carey-hr-empty">Loading…</span>
      </div>
    );
  }

  const summary = getHealthSummary(patientData);
  const sections = Object.keys(SUMMARY_SECTION_DATA).filter(
    (section) => Object.keys(summary[section]).length > 0,
  );

  return (
    <div className="carey-artifact carey-health-record">
      {sections.length === 0 ? (
        <span className="carey-hr-empty">No records on file</span>
      ) : (
        sections.map((section) => (
          <SummaryCard
            key={section}
            title={SUMMARY_SECTION_DATA[section].title}
            icon={SUMMARY_SECTION_DATA[section].icon}
            data={summary[section]}
          />
        ))
      )}
    </div>
  );
};

// Inline appointment cards. The artifact carries a small bounded projection (id + a few display
// fields) — never full appointment objects. We resolve each id to the full Appointment already in
// Redux (loading the patient's appointments if needed) and render the patient app's own
// NewAppointmentBadge, so it matches the Appointments list exactly. If the full object isn't
// available (e.g. a dependent's appointment not yet loaded), we fall back to the bounded projection.
// Shared Carey-styled record row: accent icon circle + body (title + optional subtitle + meta).
// Used by the appointment / document / immunization cards so every Carey record reads with the
// same visual language as the other Carey custom cards (handoff, subscription, etc.).
//
// Three interaction modes (mutually exclusive):
//   - read-only: omit `onClick` and `action` → static row.
//   - whole-row click: pass `onClick` → the entire row is the button (e.g. document → expand).
//   - trailing action button: pass `action={<button>…</button>}` → row is static, the action
//     button on the right is the only clickable target (matches the subscription/Open-PDF
//     pattern). If both are provided, `action` takes precedence and the row is not clickable.
const CareyRecordBadge = ({ icon, title, subtitle, onClick, action }) => {
  const hasAction = !!action;
  const isButton = !!onClick && !hasAction;
  const Tag = isButton ? 'button' : 'div';
  const className = ['carey-record-badge', isButton && 'is-button', hasAction && 'has-action']
    .filter(Boolean)
    .join(' ');
  return (
    <Tag
      type={isButton ? 'button' : undefined}
      className={className}
      onClick={isButton ? onClick : undefined}
    >
      <div className="carey-record-badge__icon" aria-hidden="true">
        <Icon src={icon} width="16px" height="16px" alt="" />
      </div>
      <div className="carey-record-badge__body">
        <span className="carey-record-badge__title">{title}</span>
        {subtitle && <span className="carey-record-badge__subtitle">{subtitle}</span>}
      </div>
      {hasAction && <div className="carey-record-badge__action">{action}</div>}
    </Tag>
  );
};

// Appointment row: not clickable. All secondary fields (datetime · physician · status) are
// merged into a single inline subtitle so the whole row reads on one line (wraps on narrow).
const CareyAppointmentBadge = ({ appt }) => {
  const physician = useAppSelector((state) =>
    appt.physician_id ? state.physicians.byId[appt.physician_id] : undefined,
  );
  const description = AppointmentUtils.getAppointmentDescription(appt);
  const dateStr = appt.date ? CalendarUtils.format(appt.date, 'ddd, MMM Do · h:mm A') : 'Date TBD';
  const physicianName = physician ? StaffUtils.getName(physician) : undefined;
  const subtitle = [dateStr, physicianName ? `with ${physicianName}` : null, appt.status]
    .filter(Boolean)
    .join(' · ');
  return (
    <CareyRecordBadge
      icon={AppointmentUtils.getPatientIcon(appt) || 'calendar'}
      title={description}
      subtitle={subtitle || undefined}
    />
  );
};

// Same Carey-styled row, populated from the bounded artifact projection when the full Appointment
// isn't in Redux yet (e.g. a dependent's appointment we haven't fetched). Read-only.
const CareyAppointmentFallback = ({ appt }) => {
  const dateStr = appt.date ? CalendarUtils.format(appt.date, 'ddd, MMM Do · h:mm A') : 'Date TBD';
  const subtitle = [
    dateStr,
    appt.physician_name ? `with ${appt.physician_name}` : null,
    appt.status,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <CareyRecordBadge
      icon={AppointmentUtils.getPatientIcon(appt) || 'calendar'}
      // Use the shared delivery-label helper (handles phone, stored as type=Video +
      // telemedicine_method=phone) instead of the bare type, which mislabels phone as "Video".
      title={AppointmentUtils.getAppointmentDescription(appt) || 'Appointment'}
      subtitle={subtitle || undefined}
    />
  );
};

const documentIconFor = (document) => {
  const map = {
    [DocumentUtils.DOCUMENT_TYPE.incoming_referral]: 'user-doctor',
    [DocumentUtils.DOCUMENT_TYPE.referral]: 'user-doctor',
    [DocumentUtils.DOCUMENT_TYPE.diagnostic]: 'stethoscope',
  };
  return map[document.document_type ?? ''] || 'file-medical';
};

// Document row: clickable (used as the collapsed header of InlineRecordExpander — tap toggles).
// Subtitle merges document_type + date into one inline string to keep the row on one line.
const CareyDocumentBadge = ({ document, onClick }) => {
  const dateStr = document.date ? CalendarUtils.format(document.date, 'MMM D, YYYY') : undefined;
  const subtitle = [document.document_type, dateStr].filter(Boolean).join(' · ');
  return (
    <CareyRecordBadge
      icon={documentIconFor(document)}
      title={document.title || 'Document'}
      subtitle={subtitle || undefined}
      onClick={onClick}
    />
  );
};

// Immunization row: title + trailing "Open PDF" button (sub-card pattern). No subtitle — the
// button label already states the action, and the goal is a single-line row matching the sub.
const CareyImmunizationBadge = ({ onClick }) => (
  <CareyRecordBadge
    icon="vial-virus"
    title="Immunization record"
    action={
      <button type="button" className="carey-record-badge__primary-action" onClick={onClick}>
        Open PDF
      </button>
    }
  />
);

const CareyAppointmentsCard = ({ message }) => {
  const dispatch = useAppDispatch();
  const activeUserId = useAppSelector((state) => state.authentication.activeUserId);
  const allAppointments = useAppSelector((state) => state.appointments.byId);

  const refs = getArtifactPayload(message)?.appointments ?? [];

  const findAppointment = (id) => {
    for (const bucket of Object.values(allAppointments)) {
      const found = (bucket || []).find((a) => a.id === id);
      if (found) return found;
    }
    return undefined;
  };

  useEffect(() => {
    // Broad sweep: load the active user's appointments (covers the common case).
    if (activeUserId) {
      dispatch(api.Appointments.getOwnAppointments({ userId: activeUserId, limit: 1000 }));
    }
    // Targeted fetch for any referenced appointment not yet in Redux — handles cross-patient refs
    // (e.g. a dependent's appointment surfaced to the master) so we always converge to the full
    // CareyAppointmentBadge rather than the bounded projection fallback.
    refs.forEach((ref) => {
      if (ref?.id && !findAppointment(ref.id)) {
        dispatch(api.Appointments.getAppointment({ appointmentId: ref.id }));
      }
    });
    // refs is a fresh array per render; depend on a stable key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUserId, refs.map((r) => r?.id).join(',')]);

  if (!refs.length) return null;

  return (
    <div className="carey-artifact carey-records">
      {refs.map((ref) => {
        const full = findAppointment(ref.id);
        return full ? (
          <CareyAppointmentBadge key={ref.id} appt={full} />
        ) : (
          <CareyAppointmentFallback key={ref.id} appt={ref} />
        );
      })}
    </div>
  );
};

// Read-only document references rendered with the patient app's own DefaultRecordBadge — the same
// row used in the Health Records timeline — so Carey's document list matches the rest of the app.
// The artifact carries only ids; each is resolved to the full document in Redux (fetched on demand),
// and tapping navigates to the patient-facing record detail (`/records/:patientId/documents/:id` on
// the HR stack), the same place RecordsTimeline sends the patient.
const CareyDocumentsCard = ({ message }) => {
  const refs = getArtifactPayload(message)?.documents ?? [];
  if (!refs.length) return null;
  return (
    <div className="carey-artifact carey-records">
      {refs.map((ref) => (
        <CareyDocumentRecordBadge key={ref.id} documentId={ref.id} />
      ))}
    </div>
  );
};

const CareyDocumentRecordBadge = ({ documentId }) => {
  const dispatch = useAppDispatch();
  const document = useAppSelector((state) =>
    state.documents.all.find((it) => it.id === documentId),
  );

  useEffect(() => {
    if (!document) dispatch(api.Documents.getDocument({ documentId }));
  }, [document, documentId]);

  if (!document) return null;

  return (
    <InlineRecordExpander
      markType="PatientDocument"
      record={document}
      renderBadge={({ toggle }) => <CareyDocumentBadge document={document} onClick={toggle} />}
      // DocumentItems is the same component the records page renders at
      // /records/:patientId/documents/:id — surfaces the existing Open-PDF / Download / results
      // viewer affordances inline. Its styles in Detail/style.scss are nested under
      // `.hr-detail-page`, so we wrap with that class to inherit the practitioner-badge alignment
      // and full-width button row (we override its `.hr-detail-card` background separately to
      // avoid the nested-card look in this context).
      renderDetail={() => (
        <div className="hr-detail-page">
          <DocumentItems document={document} />
        </div>
      )}
    />
  );
};

// Immunization record card: a single Carey-styled row; the artifact carries only the patient_id
// and tapping opens the on-demand immunization PDF (existing exportImmunizations endpoint) in the
// standard preview modal — nothing is persisted server-side.
const CareyImmunizationsCard = ({ message }) => {
  const dispatch = useAppDispatch();
  const payload = getArtifactPayload(message);
  const patientId = payload?.patient_id;
  const [showModal, setShowModal] = useState(false);

  if (!patientId) return null;

  const getFile = (dispatchOptions) =>
    dispatch(api.HealthRecords.exportImmunizations({ patientId }), dispatchOptions);

  return (
    <div className="carey-artifact carey-records">
      <CareyImmunizationBadge onClick={() => setShowModal(true)} />
      {showModal && (
        <DefaultModal
          className="modal health-record-modal large-modal"
          wrap={true}
          padding={false}
          content={<LargeResourcePreview autoLoad={true} getFile={getFile} />}
          isOpen={showModal}
          onRequestClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

// Passive system card shown when Carey hands the chat off to a human team member. The room flips
// from room_type 'carey' to 'helpdesk' server-side; this artifact gives the patient an in-thread
// confirmation that the request landed. Read-only — no actions, no tap target.
const CareyHandoffCard = ({ message }) => {
  const payload = getArtifactPayload(message);
  const handedOffAt = payload?.handed_off_at;
  // staff_summary is a STAFF-ONLY triage summary of why the chat was handed off, emitted as
  // `|`-separated "Label: value" sections (same format as the helpdesk carey_summary).
  const summarySections = parseCareySummarySections(payload?.staff_summary);
  return (
    <div className="carey-artifact carey-handoff">
      <div className="carey-handoff-icon" aria-hidden="true">
        <Icon src="users" width="18px" height="18px" alt="" />
      </div>
      <div className="carey-handoff-body">
        <div className="carey-handoff-title">Handed off to staff</div>
        <div className="carey-handoff-text">A team member will pick this up shortly.</div>
        {summarySections.length > 0 && (
          <div className="carey-handoff-summary">
            <div className="carey-handoff-summary-label">Summary for staff</div>
            {summarySections.map((section, index) => (
              <div className="carey-handoff-summary-section" key={index}>
                {section.label && (
                  <span className="carey-handoff-summary-field">{section.label}</span>
                )}
                <span className="carey-handoff-summary-text">{section.value}</span>
              </div>
            ))}
          </div>
        )}
        {handedOffAt && (
          <div className="carey-handoff-date">{CalendarUtils.format(handedOffAt, dateFormat)}</div>
        )}
      </div>
    </div>
  );
};

// Launcher card for changing the patient's primary practitioner. Opens the existing
// ChoosePrimary widget in a modal; that widget makes the change itself (api.Patients.updatePatient).
// On success we close the loop by approving the artifact (server no-op that just flips the card to
// resolved via the normal approval lifecycle). Self-service — no staff involvement.
const CareyChoosePrimaryCard = ({ message }) => {
  const dispatch = useAppDispatch();
  const requestId = getArtifactId(message);
  const patient = useAppSelector(ActivePatient);
  const [status, setStatus] = useState(getArtifactStatus(message) || CAREY_APPROVAL_STATUS.pending);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [chosenName, setChosenName] = useState(undefined);
  const done =
    status === CAREY_APPROVAL_STATUS.executed || status === CAREY_APPROVAL_STATUS.approved;

  const onSuccess = async (practitioner) => {
    setOpen(false);
    const name = [practitioner?.first_name, practitioner?.last_name].filter(Boolean).join(' ');
    if (name) setChosenName(name);
    if (busy || !requestId || done) return;
    setBusy(true);
    const result = await dispatch(
      api.Chat.respondToCareyApproval({
        requestId,
        careyApprovalDecision: { decision: 'approve' },
      }),
      { errorTitle: 'Carey' },
    );
    if (result?.status) setStatus(result.status);
    setBusy(false);
  };

  return (
    <div className="carey-artifact carey-handoff carey-choose-primary">
      <div className="carey-handoff-icon" aria-hidden="true">
        <Icon src="user-doctor" width="18px" height="18px" alt="" />
      </div>
      <div className="carey-handoff-body">
        {done ? (
          <>
            <div className="carey-handoff-title">Primary practitioner updated</div>
            <div className="carey-handoff-text">
              {chosenName
                ? `Your primary practitioner is now ${chosenName}.`
                : 'Your primary practitioner has been set.'}
            </div>
          </>
        ) : (
          <>
            <div className="carey-handoff-title">Choose your primary practitioner</div>
            <button
              type="button"
              className="carey-choose-primary-btn"
              disabled={busy || !patient}
              onClick={() => setOpen(true)}
            >
              Choose practitioner
            </button>
          </>
        )}
      </div>
      <DefaultPopup
        openHandler={[open, setOpen]}
        title="Choose your primary practitioner"
        className="carey-launcher-popup"
      >
        {patient ? (
          <ChoosePrimaryPage
            pageContentOnly
            fullHeightPopup
            patient={patient}
            onSuccess={onSuccess}
          />
        ) : null}
      </DefaultPopup>
    </div>
  );
};

// Launcher card for joining the cancellation list (waitlist). Opens the shared CancellationListForm
// in a modal; that form submits to the cancellation list itself. On submit we close the loop by
// approving the artifact (server no-op that flips the card to resolved). Self-service — no staff.
const CareyCancellationListCard = ({ message }) => {
  const dispatch = useAppDispatch();
  const requestId = getArtifactId(message);
  const payload = getArtifactPayload(message);
  const patient = useAppSelector(ActivePatient);
  const [status, setStatus] = useState(getArtifactStatus(message) || CAREY_APPROVAL_STATUS.pending);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const done =
    status === CAREY_APPROVAL_STATUS.executed || status === CAREY_APPROVAL_STATUS.approved;

  const onSubmitted = async () => {
    setOpen(false);
    if (busy || !requestId || done) return;
    setBusy(true);
    const result = await dispatch(
      api.Chat.respondToCareyApproval({
        requestId,
        careyApprovalDecision: { decision: 'approve' },
      }),
      { errorTitle: 'Carey' },
    );
    if (result?.status) setStatus(result.status);
    setBusy(false);
  };

  return (
    <div className="carey-artifact carey-handoff carey-choose-primary">
      <div className="carey-handoff-icon" aria-hidden="true">
        <Icon src="bell" width="18px" height="18px" alt="" />
      </div>
      <div className="carey-handoff-body">
        {done ? (
          <>
            <div className="carey-handoff-title">You're on the cancellation list</div>
            <div className="carey-handoff-text">We'll reach out if an earlier spot opens up.</div>
          </>
        ) : (
          <>
            <div className="carey-handoff-title">Join the cancellation list</div>
            <button
              type="button"
              className="carey-choose-primary-btn"
              disabled={busy || !patient}
              onClick={() => setOpen(true)}
            >
              Set my preferences
            </button>
          </>
        )}
      </div>
      <DefaultPopup
        openHandler={[open, setOpen]}
        title="Join Cancellation List"
        className="cancellation-list-modal carey-launcher-popup"
      >
        {patient ? (
          <CancellationListForm
            patient={patient}
            extraInfo={payload?.reason}
            onCancel={() => setOpen(false)}
            onSubmitted={onSubmitted}
          />
        ) : null}
      </DefaultPopup>
    </div>
  );
};

// Subscription-status card surfaced by carey_show_subscription. Payload carries plan/status/renewal;
// the card renders the appropriate CTA (Subscribe / Manage / Resume) so the patient can act without
// leaving the chat. Carey can't change the subscription itself — taps open the existing app flow.
const CareySubscriptionCard = ({ message }) => {
  const nav = useNavStack(Stacks.Master);
  const payload = getArtifactPayload(message);
  const isSubscribed = !!payload?.is_subscribed;
  const planName = payload?.plan_name;
  const renewalDisplay = payload?.next_renewal_date_display;
  const cancelingAtPeriodEnd = !!payload?.cancel_at_period_end;

  let title;
  let detail;
  let primaryLabel;
  let primaryRoute;
  if (!isSubscribed) {
    title = "You're on pay-per-use.";
    detail = 'Subscribe for unlimited visits and member perks.';
    primaryLabel = 'Subscribe';
    primaryRoute = '/subscribe';
  } else if (cancelingAtPeriodEnd) {
    title = planName ? `${planName} — canceling` : 'Membership canceling';
    detail = renewalDisplay
      ? `Set to cancel on ${renewalDisplay}.`
      : 'Set to cancel at the end of the current period.';
    primaryLabel = 'Resume';
    primaryRoute = '/billing';
  } else {
    title = planName ?? 'Active membership';
    detail = renewalDisplay ? `Renews ${renewalDisplay}.` : 'Active.';
    primaryLabel = 'Manage';
    primaryRoute = '/billing';
  }

  return (
    <div className="carey-artifact carey-subscription">
      <div className="carey-subscription-icon" aria-hidden="true">
        <Icon src="credit-card" width="16px" height="16px" alt="" />
      </div>
      <div className="carey-subscription-body">
        <span className="carey-subscription-title">{title}</span>
        {detail && <span className="carey-subscription-detail">{detail}</span>}
      </div>
      <div className="carey-subscription-actions">
        <button type="button" onClick={() => nav.push(primaryRoute)}>
          {primaryLabel}
        </button>
      </div>
    </div>
  );
};

// Passive confirmation card shown when the patient uploads a file into a Carey chat. The file is
// queued for staff review; there's nothing for the patient to open here (they just sent it), so the
// card is read-only — no tap target, no actions. Mirrors the handoff card layout.
const CareyDocumentReceivedCard = ({ message }) => {
  const payload = getArtifactPayload(message);
  const title = payload?.title || 'Document';
  return (
    <div className="carey-artifact carey-document-received">
      <div className="carey-document-received-icon" aria-hidden="true">
        <Icon src="file" width="18px" height="18px" alt="" />
      </div>
      <div className="carey-document-received-body">
        <div className="carey-document-received-title">{title}</div>
      </div>
    </div>
  );
};

const Section = ({ title, content }) => {
  if (title === 'Patient' && content.startsWith('patientId(')) {
    const id = /patientId\((.*)\)/.exec(content)?.[1];
    if (!id) return null;
    return <PatientBadge id={id} goToBubble={true} goToProfile={true} />;
  }
  if (title === 'Document' && content.startsWith('documentId(')) {
    const documentId = /documentId\((.*)\)/.exec(content)?.[1];
    if (!documentId) {
      return null;
    }
    return <LazyDocumentBadge documentId={documentId} />;
  }
  return (
    content !== 'N/A' && (
      <DefaultTooltip placement="right" title={content}>
        <div className="message-section">
          <Icon src="file" width="15px" />
          {title}
        </div>
      </DefaultTooltip>
    )
  );
};

// Renders the patient app's own DocumentBadge for a document referenced from chat, lazily fetching
// the full document + patient. Tap navigates to the patient's record-detail page
// (`/records/:patientId/documents/:id`) which uses the patient-facing DocumentItems viewer
// (HtmlReportViewer + Download/Open File buttons via LargeResourcePreview). We deliberately do NOT
// open `DocumentDetailsModal` here: that modal renders staff-style edit/archive/hide/pin controls
// (DocumentDetails view) and is not patient-safe. Used both for inline `documentId(...)` chat
// references and for Carey's documents artifact card, so both surfaces stay consistent.
const LazyDocumentBadge = ({ documentId }) => {
  const dispatch = useAppDispatch();
  const document = useAppSelector((state) =>
    state.documents.all.find((it) => it.id === documentId),
  );
  const patient = useAppSelector((state) => state.patients.byId[document?.patient_id ?? '']);
  const hrNav = useNavStack(Stacks.HR);
  const { switchTab } = useNavTab();

  useEffect(() => {
    if (!document) {
      dispatch(api.Documents.getDocument({ documentId }));
    }
  }, [document, documentId]);

  useEffect(() => {
    if (document && !patient) {
      dispatch(api.Patients.getPatient({ id: document.patient_id }));
    }
  }, [document, patient]);

  if (!document || !patient) {
    return null;
  }

  const openDocument = () => {
    hrNav.push(`/records/${document.patient_id}/documents/${document.id}`);
    switchTab(Tabs.HR);
  };

  return <DocumentBadge patient={patient} item={document} onClick={openDocument} />;
};

// Markdown (GFM) tables render as real <table>s; wrap them so wide tables scroll horizontally
// inside the narrow chat column instead of overflowing the bubble.
const ChatTable = ({ children }) => (
  <div className="chat-table-wrapper">
    <table>{children}</table>
  </div>
);

const TextMessage = ({ message }) => {
  const text = message.message || message.text || 'N/A';
  return (
    <ErrorBoundary content={text}>
      <ReactMarkdown
        components={{ p: Paragraph, li: Li, code: Code, blockquote: Blockquote, table: ChatTable }}
        remarkPlugins={[remarkGfm]}
      >
        {text}
      </ReactMarkdown>
    </ErrorBoundary>
  );
};

// Linkify bare URLs in paragraph text (markdown links are already handled by ReactMarkdown).
const Paragraph = ({ children }) => (
  <Linkify options={{ target: '_blank', rel: 'noopener noreferrer' }}>
    <p>{children}</p>
  </Linkify>
);

const formatJsonForDisplay = (jsonData) => {
  const formatValue = (value, indent = 0) => {
    const spacing = '  '.repeat(indent);

    if (Array.isArray(value)) {
      if (value.length === 0) return '[]';
      return value.map((item) => `${spacing}- ${formatValue(item, indent + 1)}`).join('\n');
    }

    if (typeof value === 'object' && value !== null) {
      if (Object.keys(value).length === 0) return '{}';
      return Object.entries(value)
        .map(([key, val]) => {
          const formattedKey =
            key.charAt(0).toUpperCase() +
            key
              .slice(1)
              .replace(/([A-Z_])/g, ' $1')
              .trim();
          return `${spacing}**${formattedKey}:** ${formatValue(val, indent + 1)}`;
        })
        .join('\n');
    }

    return String(value);
  };

  return `> ${formatValue(jsonData).replace(/\n/g, '\n> ')}`;
};

const MarkdownMessage = ({ message }) => {
  const finalMessage = useMemo(() => {
    let text = message.text;
    //if (!text.includes("JSON")) return message.text;
    const jsonBlockRegex = /JSON\([^)]*\)/g;
    let jsonParts = [];
    let match;
    match = jsonBlockRegex.exec(text);
    while (match !== null) {
      jsonParts.push(match[0]);
      match = jsonBlockRegex.exec(text);
    }

    if (jsonParts.length > 0) {
      let combinedJson = jsonParts
        .map((part) => part.replace(/^JSON\(/, '').replace(/\)$/, ''))
        .join('');

      // Try to parse as single JSON first
      try {
        const parsedJson = JSON.parse(combinedJson);
        text = text.replace(jsonBlockRegex, '');
        const humanReadableContent = formatJsonForDisplay(parsedJson);
        text = `${humanReadableContent}\n\n${text}`;
      } catch (e) {
        // If single JSON fails, try to split into multiple JSON objects
        try {
          const jsonObjects = [];
          let currentJson = '';
          let braceCount = 0;
          let inString = false;
          let escapeNext = false;

          for (let i = 0; i < combinedJson.length; i++) {
            const char = combinedJson[i];
            currentJson += char;

            if (escapeNext) {
              escapeNext = false;
              continue;
            }

            if (char === '\\') {
              escapeNext = true;
              continue;
            }

            if (char === '"' && !escapeNext) {
              inString = !inString;
              continue;
            }

            if (!inString) {
              if (char === '{') {
                braceCount++;
              } else if (char === '}') {
                braceCount--;
                if (braceCount === 0) {
                  // Complete JSON object found
                  jsonObjects.push(JSON.parse(currentJson));
                  currentJson = '';
                }
              }
            }
          }

          if (jsonObjects.length > 0) {
            text = text.replace(jsonBlockRegex, '');
            const humanReadableContent = jsonObjects
              .map((obj) => formatJsonForDisplay(obj))
              .join('\n\n');
            text = `${humanReadableContent}\n\n${text}`;
          } else {
            text = text.replace(jsonBlockRegex, '');
          }
        } catch (splitError) {
          text = text.replace(jsonBlockRegex, '');
        }
      }
    }

    return text;
  }, [message.text]);

  return (
    <ErrorBoundary>
      <ReactMarkdown
        components={{ li: Li, code: Code, blockquote: Blockquote, table: ChatTable }}
        remarkPlugins={[remarkGfm]}
      >
        {finalMessage}
      </ReactMarkdown>
    </ErrorBoundary>
  );
};

const Blockquote = ({ children }) => (
  <div className="code-container">
    <code>{children}</code>
  </div>
);

export const Code = ({ children }) => {
  const codeRef = useRef(null);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (codeRef.current) {
      navigator.clipboard.writeText(codeRef.current.textContent).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 5000);
      });
    }
  };

  return (
    <div className="code-container">
      <code ref={codeRef}>{children}</code>
      <button onClick={handleCopy} className="copy-button">
        <Icon src={copied ? 'check' : 'copy'} width="15px" alt={copied ? 'Copied' : 'Copy'} />
        <span>{copied ? 'Copied!' : 'Copy to Clipboard'}</span>
      </button>
    </div>
  );
};

export const Li = ({ children }) => {
  const ref = useRef();
  const [index, setIndex] = useState(null);

  useEffect(() => {
    const parent = ref.current.parentElement;
    const index = Array.from(parent.children).indexOf(ref.current);
    const tag = parent.tagName.toLowerCase();
    //get "start" attribute of parent:
    const start = parent.getAttribute('start');
    if (tag === 'ol') {
      setIndex(parseInt(start || '1') + index);
    }
  }, []);
  return (
    <Linkify options={{ target: '_blank', rel: 'noopener noreferrer' }}>
      <li ref={ref} className="markdown-li">
        &nbsp;&nbsp;
        <span className="selectable-li">{index ? <>{index}.</> : <>-</>}&nbsp;</span>
        {children}
      </li>
    </Linkify>
  );
};

const MediaMessage = ({ message, patient, roomName }) => (
  <ErrorBoundary>
    <MediaChatMessage message={message} patient={patient} roomName={roomName} />
  </ErrorBoundary>
);

function isMediaMessage(message) {
  return (message.text || message.message || '').startsWith(FILE_CMD);
}

ChatMessage.propTypes = {
  name: PropTypes.string,
  author: PropTypes.string,
  date: PropTypes.number,
  messages: PropTypes.array,
  own: PropTypes.bool,
};

export default ChatMessage;
